import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Calendar, MapPin, Clock, Eye, CheckCircle,
  XCircle, AlertCircle, Loader2, Home, PoundSterling
} from "lucide-react";
import { API } from "@/App";

const getBuyerAuth = () => {
  const token = localStorage.getItem("buyer_token");
  const applicant = JSON.parse(localStorage.getItem("buyer_applicant") || "{}");
  return { token, applicant };
};

const statusConfig = {
  requested: { label: "Requested", color: "bg-amber-100 text-amber-700", icon: Clock },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-700", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-700", icon: Eye },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: XCircle },
  no_show: { label: "No Show", color: "bg-red-100 text-red-700", icon: XCircle },
  feedback_required: { label: "Feedback Required", color: "bg-orange-100 text-orange-700", icon: AlertCircle }
};

export default function BuyerViewings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [viewings, setViewings] = useState([]);
  const { token, applicant } = getBuyerAuth();
  
  // Feedback modal
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [feedbackAction, setFeedbackAction] = useState("");
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [offerAmount, setOfferAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/buyer/login");
      return;
    }
    fetchViewings();
  }, [token]);

  const fetchViewings = async () => {
    try {
      const response = await axios.get(`${API}/buyer-portal/viewings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { upcoming: [], completed: [], past: [], total: 0 }
      const data = response.data;
      const allViewings = [
        ...(data.upcoming || []),
        ...(data.completed || []),
        ...(data.past || [])
      ];
      setViewings(allViewings);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("buyer_token");
        localStorage.removeItem("buyer_applicant");
        navigate("/buyer/login");
        return;
      }
      toast.error("Failed to load viewings");
    } finally {
      setLoading(false);
    }
  };

  const openFeedbackModal = (viewing) => {
    setSelectedViewing(viewing);
    setFeedbackAction("");
    setFeedbackNotes("");
    setOfferAmount("");
    setFeedbackModal(true);
  };

  const submitFeedback = async () => {
    if (!feedbackAction) {
      toast.error("Please select an action");
      return;
    }
    
    if (feedbackAction === "offer" && !offerAmount) {
      toast.error("Please enter an offer amount");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        viewing_id: selectedViewing.viewing_id,
        action: feedbackAction,
        notes: feedbackNotes,
        offer_amount: feedbackAction === "offer" ? parseFloat(offerAmount) : null
      };
      
      await axios.post(
        `${API}/buyer-portal/viewings/${selectedViewing.viewing_id}/feedback`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        feedbackAction === "offer" 
          ? "Offer submitted successfully!" 
          : "Feedback submitted successfully!"
      );
      setFeedbackModal(false);
      fetchViewings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  // Categorize viewings
  const upcomingViewings = viewings.filter(v => 
    v.status === "requested" || v.status === "confirmed" || v.status === "pending"
  );
  const completedViewings = viewings.filter(v => 
    v.status === "completed" || v.status === "feedback_required"
  );
  const cancelledViewings = viewings.filter(v => 
    v.status === "cancelled" || v.status === "no_show"
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/buyer/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">My Viewings</h1>
            <p className="text-sm text-muted-foreground">
              Track and manage your property viewings
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{upcomingViewings.length}</p>
              <p className="text-sm text-muted-foreground">Upcoming</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Eye className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{completedViewings.length}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Home className="w-6 h-6 mx-auto mb-2 text-slate-500" />
              <p className="text-2xl font-bold">{viewings.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="upcoming" className="space-y-4">
          <TabsList className="w-full">
            <TabsTrigger value="upcoming" className="flex-1">
              Upcoming ({upcomingViewings.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex-1">
              Completed ({completedViewings.length})
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="flex-1">
              Cancelled ({cancelledViewings.length})
            </TabsTrigger>
          </TabsList>

          {/* Upcoming Viewings */}
          <TabsContent value="upcoming" className="space-y-3">
            {upcomingViewings.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No upcoming viewings</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Browse properties and book a viewing
                  </p>
                  <Button onClick={() => navigate("/buyer/properties")}>
                    Search Properties
                  </Button>
                </CardContent>
              </Card>
            ) : (
              upcomingViewings.map((viewing) => (
                <ViewingCard 
                  key={viewing.viewing_id} 
                  viewing={viewing} 
                  onFeedback={openFeedbackModal}
                />
              ))
            )}
          </TabsContent>

          {/* Completed Viewings */}
          <TabsContent value="completed" className="space-y-3">
            {completedViewings.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No completed viewings yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Your completed viewings will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              completedViewings.map((viewing) => (
                <ViewingCard 
                  key={viewing.viewing_id} 
                  viewing={viewing}
                  onFeedback={openFeedbackModal}
                  showFeedbackButton={viewing.status === "feedback_required" || viewing.status === "completed"}
                />
              ))
            )}
          </TabsContent>

          {/* Cancelled Viewings */}
          <TabsContent value="cancelled" className="space-y-3">
            {cancelledViewings.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <XCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No cancelled viewings</h3>
                </CardContent>
              </Card>
            ) : (
              cancelledViewings.map((viewing) => (
                <ViewingCard key={viewing.viewing_id} viewing={viewing} />
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Feedback Modal */}
      <Dialog open={feedbackModal} onOpenChange={setFeedbackModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Viewing Feedback</DialogTitle>
            <DialogDescription>
              What did you think of {selectedViewing?.property_address}?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Your Decision</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={feedbackAction === "interested" ? "default" : "outline"}
                  onClick={() => setFeedbackAction("interested")}
                  className="h-auto py-3"
                >
                  <div className="text-center">
                    <CheckCircle className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">Interested</span>
                  </div>
                </Button>
                <Button
                  variant={feedbackAction === "offer" ? "default" : "outline"}
                  onClick={() => setFeedbackAction("offer")}
                  className="h-auto py-3"
                >
                  <div className="text-center">
                    <PoundSterling className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">Make Offer</span>
                  </div>
                </Button>
                <Button
                  variant={feedbackAction === "not_interested" ? "default" : "outline"}
                  onClick={() => setFeedbackAction("not_interested")}
                  className="h-auto py-3"
                >
                  <div className="text-center">
                    <XCircle className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">Not For Me</span>
                  </div>
                </Button>
              </div>
            </div>
            
            {feedbackAction === "offer" && (
              <div>
                <Label htmlFor="offerAmount">Offer Amount (£)</Label>
                <div className="relative mt-1">
                  <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="offerAmount"
                    type="number"
                    placeholder="Enter your offer"
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(e.target.value)}
                    className="pl-9"
                    data-testid="offer-amount-input"
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Share your thoughts about the property..."
                value={feedbackNotes}
                onChange={(e) => setFeedbackNotes(e.target.value)}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitFeedback} 
              disabled={submitting || !feedbackAction}
              data-testid="submit-feedback-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {feedbackAction === "offer" ? "Submit Offer" : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Viewing Card Component
function ViewingCard({ viewing, onFeedback, showFeedbackButton = false }) {
  const status = statusConfig[viewing.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const viewingDate = viewing.requested_date || viewing.viewing_date;
  
  return (
    <Card data-testid={`viewing-${viewing.viewing_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={status.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
            </div>
            <h3 className="font-medium mb-1">{viewing.property_address || "Property"}</h3>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {viewingDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(viewingDate).toLocaleDateString()}
                </span>
              )}
              {viewing.requested_time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {viewing.requested_time}
                </span>
              )}
            </div>
            {viewing.asking_price && (
              <p className="text-sm font-medium text-primary mt-2">
                £{viewing.asking_price.toLocaleString()}
              </p>
            )}
          </div>
          
          {(showFeedbackButton || viewing.status === "completed" || viewing.status === "feedback_required") && onFeedback && (
            <Button 
              variant="outline" 
              onClick={() => onFeedback(viewing)}
              data-testid={`feedback-btn-${viewing.viewing_id}`}
            >
              Give Feedback
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
